import React from 'react';
import {
  Typography, Button, Dialog, DialogActions, DialogContent, IconButton, Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LocalText from '../common/LocalText';

const SEVERITY = {
  warning: { Icon: WarningAmberIcon,      iconColor: '#d97706', iconBg: '#fef3c7', headerBg: '#fffbeb', headerBorder: '#fde68a', confirmBg: '#d97706', confirmHover: '#b45309' },
  info:    { Icon: InfoOutlinedIcon,      iconColor: '#5383b3', iconBg: '#dbeafe', headerBg: '#eff6ff', headerBorder: '#bfdbfe', confirmBg: '#0a326d', confirmHover: '#1a4a8a' },
  success: { Icon: CheckCircleOutlineIcon,iconColor: '#16a34a', iconBg: '#dcfce7', headerBg: '#f0fdf4', headerBorder: '#bbf7d0', confirmBg: '#16a34a', confirmHover: '#15803d' },
  danger:  { Icon: DeleteOutlineIcon,     iconColor: '#dc2626', iconBg: '#fee2e2', headerBg: '#fff1f2', headerBorder: '#fecaca', confirmBg: '#dc2626', confirmHover: '#b91c1c' },
};

const ConfirmModal = ({
  open,
  onCancel,
  onConfirm,
  title,
  message,
  subtitle,
  confirmLabel = 'Confirm',
  cancelLabel  = 'Cancel',
  severity     = 'warning',
}) => {
  const s = SEVERITY[severity] ?? SEVERITY.warning;
  const { Icon } = s;

  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="xs" disableRestoreFocus
      PaperProps={{ sx: { borderRadius: { xs: '12px', sm: '16px' }, overflow: 'hidden', m: { xs: 1, sm: 2 } } }}
    >
      {/* Header */}
      <Box sx={{ bgcolor: s.headerBg, borderBottom: `1px solid ${s.headerBorder}`, px: { xs: 2, sm: 3 }, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ width: 30, height: 30, borderRadius: '50%', bgcolor: s.iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon sx={{ fontSize: 17, color: s.iconColor }} />
        </Box>
        <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#0a326d', flex: 1 }}>
          <LocalText>{title}</LocalText>
        </Typography>
        <IconButton size="small" onClick={(e) => { e.currentTarget.blur(); onCancel(); }}
          sx={{ color: '#94a3b8', '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' } }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Body */}
      <DialogContent sx={{ px: { xs: 2, sm: 3 }, pt: 2.5, pb: 2 }}>
        <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#0a326d', mb: subtitle ? 0.5 : 0 }}>
          <LocalText>{message}</LocalText>
        </Typography>
        {subtitle && (
          <Typography sx={{ fontSize: 13, color: '#5383b3', lineHeight: 1.5 }}>
            <LocalText>{subtitle}</LocalText>
          </Typography>
        )}
      </DialogContent>

      {/* Actions */}
      <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 2, gap: 1.5, bgcolor: '#fafafa', borderTop: '1px solid #f1f5f9' }}>
        <Button onClick={onCancel} variant="outlined"
          sx={{ flex: 1, textTransform: 'none', borderRadius: '10px', fontWeight: 600, fontSize: 13, color: '#64748b', borderColor: '#e2e8f0', '&:hover': { bgcolor: '#f1f5f9', borderColor: '#cbd5e1' } }}
        >
          <LocalText>{cancelLabel}</LocalText>
        </Button>
        <Button onClick={onConfirm} variant="contained"
          startIcon={severity === 'danger' ? <DeleteOutlineIcon sx={{ fontSize: 17 }} /> : undefined}
          sx={{ flex: 1, textTransform: 'none', borderRadius: '10px', fontWeight: 700, fontSize: 13, bgcolor: s.confirmBg, boxShadow: 'none', '&:hover': { bgcolor: s.confirmHover, boxShadow: 'none' }, transition: 'background 0.2s ease' }}
        >
          <LocalText>{confirmLabel}</LocalText>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmModal;
